import React, { useState, useEffect, useMemo, useCallback, useContext, createContext } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import DOMPurify from 'dompurify';
import ImageCarousel from '../ImageCarousel/ImageCarousel';
import Lightbox from '../Lightbox/Lightbox';
import { postsApi } from '../../services/api';
import { getVideoEmbedUrl, isImageUrl } from '../RichEditor/richEditorUtils';
import { buildContentPath, buildPostPath } from '../../utils/postUrls';
import { Link, useLocation } from 'react-router-dom';
import { useSettings } from '../../context/SettingsContext';
import { stripSuppressedSubBlocks } from '../../config/suppressedSubLinkDomains';
import { HIVE_FRONTENDS } from '../../config/hiveFrontends';
import './MarkdownRenderer.css';

// Context that signals an <img> is nested inside an <a> link.
// When true the img handler skips lightbox so the anchor click propagates normally.
const InsideLinkContext = createContext(false);

// Helper function to extract images from post metadata
const extractImagesFromPost = (postData) => {
  const images = [];
  if (postData && postData.json_metadata) {
    const metadata = typeof postData.json_metadata === 'string' 
      ? JSON.parse(postData.json_metadata) 
      : postData.json_metadata;
    if (metadata.image && Array.isArray(metadata.image)) {
      images.push(...metadata.image);
    } else if (metadata.images && Array.isArray(metadata.images)) {
      images.push(...metadata.images);
    } else if (metadata.thumbnail) {
      images.push(metadata.thumbnail);
    }
  }
  return images;
};

// Helper: Detect and render 3Speak audio player
const AUDIO_3SPEAK_REGEX = /^https?:\/\/audio\.3speak\.tv\/play\?a=([\w-]+)/i;

const renderAudio3SpeakEmbed = (href, title = '') => {
  const match = href.match(AUDIO_3SPEAK_REGEX);
  if (!match) return null;
  const audioId = match[1];
  const embedUrl = `https://audio.3speak.tv/play?a=${audioId}&mode=compact&iframe=1`;
  return (
    <div className="media__music">
      <iframe
        src={embedUrl}
        frameBorder="0"
        allowFullScreen
        loading="lazy"
        width="100%"
        height="120"
        sandbox="allow-scripts allow-same-origin allow-popups"
        title={title || '3Speak Audio'}
      />
    </div>
  );
};

// map of video provider regex -> embed URL builder
const VIDEO_PROVIDERS = [
  {
    name: 'youtube',
    regex: /(?:https?:\/\/)?(?:(?:www|m|music)\.)?(?:youtube\.com\/(?:watch\?v=|shorts\/|live\/)|youtu\.be\/)([\w-]{11})/, // capture id
    embedUrl: (id) => `https://www.youtube.com/embed/${id}`
  },
  {
    name: 'vimeo',
    regex: /(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(\d+)/,
    embedUrl: (id) => `https://player.vimeo.com/video/${id}`
  },
  {
    name: '3speak',
    // capture everything after v= until ampersand/space so we keep user/permlink
    // supports both /watch and /embed variants from 3Speak/play.3Speak
    regex: /(?:https?:\/\/)?(?:www\.)?(?:3speak\.tv|play\.3speak\.tv)\/(?:watch|embed)\?v=([^&\s]+)/,
    embedUrl: (id) => {
      // use absolute https to avoid protocol-relative issues in mobile webviews
      const safeId = String(id || '').trim().replace(/%2F/gi, '/');
      // allow responsive layout so playback matches the standard 16:9 embed experience
      return `https://play.3speak.tv/watch?v=${safeId}&mode=iframe`;
    }
  },
  {
    name: 'dtube',
    regex: /(?:https?:\/\/)?(?:www\.)?d\.tube\/#!\/v\/([\w.-]+)\/([\w.-]+)/,
    embedUrl: (match) => `https://d.tube/#!/v/${match[1]}/${match[2]}`
  },
  {
    name: 'dailymotion',
    regex: /(?:https?:\/\/)?(?:www\.)?(?:dailymotion\.com\/video\/|dai\.ly\/)([\w-]+)/,
    embedUrl: (id) => `https://www.dailymotion.com/embed/video/${id}`
  },
  {
    name: 'twitch',
    regex: /(?:https?:\/\/)?(?:www\.)?twitch\.tv\/videos\/(\d+)/,
    embedUrl: (id) => {
      const parent = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
      return `https://player.twitch.tv/?video=v${id}&parent=${encodeURIComponent(parent)}`;
    }
  }
];

const decodeShortcodeValue = (value = '') => String(value)
  .replace(/&#93;/g, ']')
  .replace(/\\"/g, '"')
  .replace(/\\\\/g, '\\');

const parseShortcodeAttributes = (input = '') => {
  const attributes = {};
  String(input).replace(/([a-zA-Z][\w-]*)="((?:\\.|[^"])*)"/g, (_match, key, value) => {
    attributes[String(key).toLowerCase()] = decodeShortcodeValue(value);
    return _match;
  });
  String(input).replace(/([a-zA-Z][\w-]*)=([^\s\]]+)/g, (_match, key, value) => {
    const lowerKey = String(key).toLowerCase();
    if (!Object.prototype.hasOwnProperty.call(attributes, lowerKey)) {
      attributes[lowerKey] = decodeShortcodeValue(value);
    }
    return _match;
  });
  return attributes;
};

const normalizeShortcodeAlignment = (value = 'left') => {
  const normalized = String(value || 'left').toLowerCase();
  if (normalized === 'center' || normalized === 'right') return normalized;
  return 'left';
};

const escapeHtml = (value = '') => String(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

const stripAutoplayFromUrl = (rawUrl = '') => {
  const value = String(rawUrl || '').trim();
  if (!value) return value;

  try {
    const parsed = new URL(value);
    parsed.searchParams.delete('autoplay');
    parsed.searchParams.delete('auto_play');
    return parsed.toString();
  } catch (_err) {
    return value;
  }
};

const stripAutoplayFromAllow = (allow = '') => {
  return String(allow || '')
    .split(';')
    .map((token) => token.trim())
    .filter((token) => token && token.toLowerCase() !== 'autoplay')
    .join('; ');
};


// Within HTML block containers (center, div, blockquote, etc.) bare newlines
// are just whitespace in HTML — insert explicit <br> so text on separate lines
// renders with visible breaks, matching Hive reference renderer behaviour.
const insertHtmlBlockLineBreaks = (text) => {
  // Block-level containers whose opening/closing tags change nesting depth
  const BLOCK_OPEN_RE  = /<(?:center|div|blockquote|details|article|aside|section|header|footer|nav|main|figure|pre)\b[^>]*>/gi;
  const BLOCK_CLOSE_RE = /<\/(?:center|div|blockquote|details|article|aside|section|header|footer|nav|main|figure|pre)>/gi;

  // Line endings that already imply a visual break — no extra <br> needed
  const NO_BR_END_RE   = /(<\/(?:center|div|p|h[1-6]|ul|ol|li|table|thead|tbody|tfoot|tr|th|td|blockquote|pre|figure|details|summary|sub|sup)>|<(?:br|hr|img|shortappvideo)\b[^>]*>)\s*$/i;

  // A line whose entire content is HTML tags with no text — structural, skip
  const PURE_TAG_LINE_RE = /^\s*(?:<[^>]+>\s*)+\s*$/;

  const lines = text.split('\n');
  let depth = 0;
  const out = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    const wasDepth = depth;
    const opens  = (line.match(BLOCK_OPEN_RE)  || []).length;
    const closes = (line.match(BLOCK_CLOSE_RE) || []).length;
    depth = Math.max(0, depth + opens - closes);

    const addBr = (
      wasDepth > 0 &&      // currently inside an HTML block
      trimmed &&           // line is not empty
      !PURE_TAG_LINE_RE.test(trimmed) &&  // line has visible text content
      !/<br\s*\/?>\s*$/i.test(trimmed) && // no existing <br> at end
      !NO_BR_END_RE.test(trimmed)         // doesn't end with a structural close tag
    );

    out.push(addBr ? line + '<br>' : line);
  }

  return out.join('\n');
};

const stripConfiguredSubAttributionBlocks = (text = '') => {
  return stripSuppressedSubBlocks(text);
};

const TABLE_SEPARATOR_RE = /^\s*\|?\s*:?-+:?\s*(?:\|\s*:?-+:?\s*)+\|?\s*$/;
const TABLE_ROW_RE = /^\s*\|.*\|\s*$/;

const normalizeTableSeparatorLine = (line = '') => {
  const raw = String(line || '');
  if (!TABLE_SEPARATOR_RE.test(raw)) return raw;

  const trimmed = raw.trim();
  const hasLeadingPipe = trimmed.startsWith('|');
  const hasTrailingPipe = trimmed.endsWith('|');
  const inner = trimmed.replace(/^\|/, '').replace(/\|$/, '');
  const cells = inner.split('|').map((part) => {
    const token = String(part || '').trim();
    const match = token.match(/^(:?)(-+)(:?)$/);
    if (!match) return token;
    const left = match[1] || '';
    const dashes = match[2] || '-';
    const right = match[3] || '';
    const normalizedDashes = '-'.repeat(Math.max(3, dashes.length));
    return `${left}${normalizedDashes}${right}`;
  });

  const rebuilt = cells.join('|');
  return `${hasLeadingPipe ? '|' : ''}${rebuilt}${hasTrailingPipe ? '|' : ''}`;
};

const normalizeMarkdownTableSpacing = (source = '') => {
  const lines = String(source || '').split('\n');
  if (lines.length < 2) return String(source || '');

  const out = [];
  let i = 0;

  while (i < lines.length) {
    const current = lines[i];
    const next = i + 1 < lines.length ? lines[i + 1] : '';
    const startsTable = TABLE_ROW_RE.test(current) && TABLE_SEPARATOR_RE.test(next);

    if (!startsTable) {
      out.push(current);
      i += 1;
      continue;
    }

    // Ensure a blank line before markdown table blocks so GFM reliably parses them.
    if (out.length > 0 && out[out.length - 1].trim() !== '') {
      out.push('');
    }

    out.push(current);
    out.push(normalizeTableSeparatorLine(next));
    i += 2;

    while (i < lines.length && TABLE_ROW_RE.test(lines[i])) {
      out.push(lines[i]);
      i += 1;
    }

    // Ensure a blank line after markdown table blocks.
    if (i < lines.length && lines[i].trim() !== '') {
      out.push('');
    }
  }

  return out.join('\n');
};

// helper that returns a JSX element for known video URLs (or null)
const renderVideoEmbed = (href, title = '') => {
  if (!href) return null;

  const sharedEmbedUrl = getVideoEmbedUrl(href);
  if (sharedEmbedUrl) {
    return (
      <div className="video-embed-wrapper">
        {title ? <div className="video-embed-title">{title}</div> : null}
        <div className="video-embed-container">
          <iframe
            src={stripAutoplayFromUrl(sharedEmbedUrl)}
            frameBorder="0"
            allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            allowFullScreen
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
            title={title || 'video'}
          />
        </div>
      </div>
    );
  }

  for (const prov of VIDEO_PROVIDERS) {
    const m = href.match(prov.regex);
    if (m) {
      // build embed URL (function may expect id or full match array)
      const embedSrc = prov.embedUrl(m.length > 2 ? m : m[1]);
      // wrap in responsive container
      return (
        <div className="video-embed-wrapper">
          {title ? <div className="video-embed-title">{title}</div> : null}
          <div className="video-embed-container">
            <iframe
              src={stripAutoplayFromUrl(embedSrc)}
              frameBorder="0"
              allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
              title={title || prov.name}
            />
          </div>
        </div>
      );
    }
  }
  return null;
};

const VideoEmbed = React.memo(({ href, title = '' }) => {
  const videoElem = renderVideoEmbed(href, title);
  return videoElem || null;
});

// Matches common image file extensions in a URL path (before any query string or fragment)
const IMAGE_EXT_REGEX = /\.(?:jpe?g|png|gif|webp|avif|svg(?:\+xml)?|bmp|tiff?)(?:[?#][^\s]*)?$/i;
const VIDEO_EXT_REGEX = /\.(?:mp4|webm|ogg|mov|m4v)(?:[?#][^\s]*)?$/i;
const POST_REF_AUTHOR_REGEX = /^[a-z0-9.-]{3,32}$/i;
const POST_REF_PERMLINK_REGEX = /^[a-z0-9-]{3,255}$/i;
const POST_REF_ALLOWED_DOMAINS = [
  ...HIVE_FRONTENDS.map(f => f.domain),
  'leofinance.io',
  'hive.blog.tr'
];
const POST_REF_RESERVED_SEGMENTS = new Set([
  'threads',
  'thread',
  'view',
  'u',
  'user',
  'post',
  'posts',
  'p',
  'c',
  'community',
  'communities',
  'feed',
  'tag',
  'tags',
  'trending',
  'new',
  'created',
  'faq',
  'about',
  'login',
  'signup'
]);

const isAllowedPostRefDomain = (host = '') => {
  const normalized = String(host || '').toLowerCase();
  return POST_REF_ALLOWED_DOMAINS.some((domain) => (
    normalized === domain || normalized.endsWith(`.${domain}`)
  ));
};

const parseHiveUrl = (text = '') => {
  const raw = String(text || '').trim();
  if (!raw) return null;

  const internalPostPath = raw.match(/^\/post\/([a-z0-9.-]{3,32})\/([a-z0-9-]{3,255})\/?$/i);
  if (internalPostPath) {
    return { type: 'post', author: internalPostPath[1], permlink: internalPostPath[2] };
  }

  const internalAtPath = raw.match(/^\/@([a-z0-9.-]{3,32})\/([a-z0-9-]{3,255})\/?$/i);
  if (internalAtPath) {
    return { type: 'post', author: internalAtPath[1], permlink: internalAtPath[2] };
  }

  const direct = raw.match(/^@?([a-z0-9.-]{3,32})\/([a-z0-9-]{3,255})$/i);
  if (direct) {
    return { type: 'post', author: direct[1], permlink: direct[2] };
  }

  try {
    const parsed = new URL(raw);
    if (!isAllowedPostRefDomain(parsed.hostname)) return null;

    const segments = parsed.pathname.split('/').filter(Boolean);
    if (segments.length === 0) return null;
    
    const normalizedSegments = segments.map((seg) => String(seg || '').toLowerCase());

    // 1. Community links (e.g., /c/hive-123456 or /created/hive-123456)
    if (segments.length >= 2 && ['c', 'created', 'trending', 'new'].includes(normalizedSegments[0])) {
      if (normalizedSegments[1].startsWith('hive-')) {
        return { type: 'community', id: normalizedSegments[1] };
      }
    }

    // 2. Tag links (e.g., /trending/art or /created/art)
    if (segments.length === 2 && ['trending', 'created', 'new', 'tag'].includes(normalizedSegments[0])) {
      if (!normalizedSegments[1].startsWith('@') && !normalizedSegments[1].startsWith('hive-')) {
        return { type: 'tag', tag: normalizedSegments[1] };
      }
    }

    // 3. User profiles (e.g., /@username)
    if (segments.length === 1 && segments[0].startsWith('@')) {
      return { type: 'user', username: segments[0].slice(1) };
    }

    // 4. Post links (existing logic)
    // Prefer explicit route patterns like /threads/view/{author}/{permlink}
    const viewIndex = normalizedSegments.lastIndexOf('view');
    if (viewIndex >= 0 && viewIndex + 2 < segments.length) {
      const viewAuthor = segments[viewIndex + 1].startsWith('@')
        ? segments[viewIndex + 1].slice(1)
        : segments[viewIndex + 1];
      const viewPermlink = segments[viewIndex + 2];
      if (POST_REF_AUTHOR_REGEX.test(viewAuthor) && POST_REF_PERMLINK_REGEX.test(viewPermlink)) {
        return { type: 'post', author: viewAuthor, permlink: viewPermlink };
      }
    }

    // Generic fallback: scan from the end and skip known route keywords.
    for (let i = segments.length - 2; i >= 0; i -= 1) {
      const authorCandidate = segments[i].startsWith('@')
        ? segments[i].slice(1)
        : segments[i];
      const permlinkCandidate = segments[i + 1];
      const authorIsReserved = POST_REF_RESERVED_SEGMENTS.has(String(authorCandidate || '').toLowerCase());
      const permlinkIsReserved = POST_REF_RESERVED_SEGMENTS.has(String(permlinkCandidate || '').toLowerCase());
      if (authorIsReserved || permlinkIsReserved) continue;
      if (POST_REF_AUTHOR_REGEX.test(authorCandidate) && POST_REF_PERMLINK_REGEX.test(permlinkCandidate)) {
        return { type: 'post', author: authorCandidate, permlink: permlinkCandidate };
      }
    }
  } catch {
    return null;
  }

  return null;
};

const isImageUrlByExtension = (url = '') => {
  if (!url) return false;
  try {
    const pathname = new URL(url).pathname;
    return IMAGE_EXT_REGEX.test(pathname);
  } catch {
    return IMAGE_EXT_REGEX.test(url);
  }
};

const isVideoUrlByExtension = (url = '') => {
  if (!url) return false;
  try {
    const pathname = new URL(url).pathname;
    return VIDEO_EXT_REGEX.test(pathname);
  } catch {
    return VIDEO_EXT_REGEX.test(url);
  }
};

const buildImageFallbackSources = (url = '') => {
  const primary = String(url || '').trim();
  if (!primary) return [];
  const fallback = /^https?:\/\//i.test(primary)
    ? `https://images.hive.blog/0x0/${primary}`
    : '';
  return Array.from(new Set([primary, fallback].filter(Boolean)));
};

const ResilientImage = ({ src, alt = '', title, className, style, onFinalError, onClick }) => {
  const candidates = useMemo(() => buildImageFallbackSources(src), [src]);
  const [sourceIndex, setSourceIndex] = useState(0);
  const activeSrc = candidates[sourceIndex] || '';

  useEffect(() => {
    setSourceIndex(0);
  }, [src]);

  if (!activeSrc) return null;

  return (
    <img
      src={activeSrc}
      alt={alt}
      title={title}
      className={className}
      style={style}
      onClick={onClick}
      onError={() => {
        if (sourceIndex + 1 < candidates.length) {
          setSourceIndex((prev) => prev + 1);
          return;
        }
        onFinalError?.();
      }}
    />
  );
};

const normalizeImageEntries = (input = []) => {
  if (!Array.isArray(input)) return [];
  const out = [];
  input.forEach((entry, index) => {
    if (typeof entry === 'string') {
      const url = entry.trim();
      if (url) out.push({ url, source: 'metadata', order: index });
      return;
    }
    if (entry && typeof entry === 'object') {
      const url = String(entry.url || entry.src || '').trim();
      if (url) out.push({ url, source: entry.source || 'metadata', order: index });
    }
  });
  return out;
};

const extractInlineImageSources = (html = '') => {
  const matches = [];
  String(html).replace(/<img\b[^>]*\bsrc\s*=\s*"([^"]+)"[^>]*>/gi, (_m, src) => {
    const url = String(src || '').trim();
    if (url) matches.push({ url, source: 'inline' });
    return _m;
  });
  return matches;
};

const extractLinkUrlsFromContent = (source = '') => {
  const urls = [];
  const text = String(source || '').replace(/(!?)\[([^\]]*)\]\((https?:\/\/[^\s)]+)\)/gi, (_m, bang = '', label = '', href = '') => {
    if (bang === '!') {
      // Keep markdown image syntax intact so image URL can still be discovered for carousel mode.
      return `![${label}](${href})`;
    }
    // Drop destination URL for markdown links so [url](url) contributes a single URL source.
    return `[${label}]`;
  });
  text.replace(/https?:\/\/[^\s<)"]+/gi, (raw) => {
    const cleaned = String(raw || '').replace(/[.,;!?]+$/, '').trim();
    if (cleaned) urls.push(cleaned);
    return raw;
  });
  return urls;
};

const IMG_TAG_REGEX = /<img\b[^>]*\bsrc\s*=\s*"([^"]+)"[^>]*>/gi;

const isWhitespaceOrLineBreakOnly = (text = '') => {
  return /^\s*(?:<br\s*\/?>\s*)*$/i.test(String(text || ''));
};

const collapseContiguousImagesToGrid = (source = '', { enabled = false, min = 2, max = 6 } = {}) => {
  const text = String(source || '');
  if (!enabled || !text) return text;

  const tags = [];
  let match;
  while ((match = IMG_TAG_REGEX.exec(text)) !== null) {
    tags.push({
      start: match.index,
      end: match.index + match[0].length,
      src: String(match[1] || '').trim(),
    });
  }
  if (tags.length < min) return text;

  const replacements = [];
  let i = 0;

  while (i < tags.length) {
    let j = i;
    while (
      j + 1 < tags.length &&
      isWhitespaceOrLineBreakOnly(text.slice(tags[j].end, tags[j + 1].start))
    ) {
      j += 1;
    }

    const run = tags.slice(i, j + 1);
    if (run.length >= min && run.length <= max) {
      // The inner while-loop already guarantees every gap within the run is
      // whitespace/br only. No further surrounding-context check is needed —
      // requiring the cluster to also be isolated from other <img> tags would
      // prevent mid-document clusters (e.g. actifit photo sets followed by
      // stat images) from ever forming a grid.
      const gridItems = run
        .map((item) => `<shortappgridimg src="${escapeHtml(item.src)}"></shortappgridimg>`)
        .join('');
      replacements.push({
        start: run[0].start,
        end: run[run.length - 1].end,
        replacement: `<shortappimagegrid count="${run.length}">${gridItems}</shortappimagegrid>`,
      });
    }

    i = j + 1;
  }

  if (replacements.length === 0) return text;

  let output = text;
  for (let k = replacements.length - 1; k >= 0; k -= 1) {
    const item = replacements[k];
    output = `${output.slice(0, item.start)}${item.replacement}${output.slice(item.end)}`;
  }
  return output;
};

const flattenNodeText = (node) => {
  if (typeof node === 'string') return node;
  if (Array.isArray(node)) return node.map(flattenNodeText).join('');
  if (React.isValidElement(node)) return flattenNodeText(node.props?.children);
  return '';
};

const flattenMarkdownAstText = (node) => {
  if (!node) return '';
  if (typeof node.value === 'string') return node.value;
  if (Array.isArray(node.children)) return node.children.map(flattenMarkdownAstText).join('');
  return '';
};

const isIgnorableParagraphAstChild = (child) => {
  if (!child) return true;
  if (child.type === 'text') {
    return String(child.value || '').trim().length === 0;
  }
  return child.type === 'element' && String(child.tagName || '').toLowerCase() === 'br';
};

const extractStandalonePostRefFromParagraphNode = (paragraphNode) => {
  const children = Array.isArray(paragraphNode?.children) ? paragraphNode.children : [];
  const meaningfulChildren = children.filter((child) => !isIgnorableParagraphAstChild(child));
  if (meaningfulChildren.length !== 1) return null;

  const onlyChild = meaningfulChildren[0];

  if (onlyChild.type === 'text') {
    return parseHiveUrl(String(onlyChild.value || '').trim());
  }

  if (onlyChild.type !== 'element' || String(onlyChild.tagName || '').toLowerCase() !== 'a') {
    return null;
  }

  const href = String(onlyChild?.properties?.href || '').trim();
  if (!href) return null;

  const linkText = flattenMarkdownAstText(onlyChild).trim();
  const hrefNoProtocol = href.replace(/^https?:\/\//i, '');
  const hrefNoTrailingSlash = href.replace(/\/$/, '');
  const isAutoLinked = !linkText || linkText === href || linkText === hrefNoProtocol || linkText === hrefNoTrailingSlash;
  if (!isAutoLinked) return null;

  return parseHiveUrl(href) || parseHiveUrl(linkText);
};

const SmartExternalLink = ({ href, children, props, suppressInlineImages = false, carouselMode = false, imageLabel = '', onOpenImage = null }) => {
  const location = useLocation();
  const matchedFeed = location?.pathname?.match(/^\/feed\/([^/]+)/);
  const activeFeedType = matchedFeed && matchedFeed[1] ? matchedFeed[1].toLowerCase() : 'flex';
  const useFeedScopedHashtags = location?.pathname?.startsWith('/feed/')
    || /\/flexdetail(?:\/|$)/.test(location?.pathname || '');
  const isHttpUrl = /^https?:\/\//i.test(String(href || ''));

  // Only auto-embed (video/image) when the link has no custom text — i.e. it's a bare URL.
  // If the author wrote [some text](url), always render it as a regular hyperlink.
  const linkText = flattenNodeText(children).trim();
  const isBareUrl = !linkText || linkText === href;

  // Audio 3Speak embed
  const embeddedAudio = useMemo(() => (isHttpUrl && isBareUrl ? renderAudio3SpeakEmbed(href, '') : null), [href, isHttpUrl, isBareUrl]);
  const embeddedVideo = useMemo(() => (isHttpUrl && isBareUrl ? renderVideoEmbed(href, '') : null), [href, isHttpUrl, isBareUrl]);
  const isDirectVideo = useMemo(() => (isHttpUrl && isBareUrl ? isVideoUrlByExtension(href) : false), [href, isHttpUrl, isBareUrl]);
  // Image: only render inline when label === href (bare URL) and URL has image extension.
  // extractLinkUrlsFromContent already strips link destinations so this is the only render path.
  const isBareImage = isHttpUrl && isBareUrl && isImageUrlByExtension(href);

  if (embeddedAudio) {
    return embeddedAudio;
  }
  if (embeddedVideo) {
    return embeddedVideo;
  }

  if (isDirectVideo) {
    return (
      <video
        className="markdown-direct-video"
        src={href}
        controls
        playsInline
        preload="metadata"
      />
    );
  }

  if (isBareImage) {
    if (suppressInlineImages) return null;
    if (carouselMode) return <span className="inline-image-placeholder">{imageLabel || '[Image]'}</span>;
    return (
      <ResilientImage
        src={href}
        alt={imageLabel || ''}
        title={imageLabel || undefined}
        style={{ maxWidth: '100%', height: 'auto', display: 'block', margin: '8px 0', cursor: 'zoom-in' }}
        onClick={() => onOpenImage?.(href)}
      />
    );
  }

  const parsedUrl = parseHiveUrl(href);
  const textLabel = flattenNodeText(children).trim();
  const hrefNoProtocol = href ? href.replace(/^https?:\/\//i, '') : '';
  const isAutoLinked = textLabel === href || textLabel === hrefNoProtocol || textLabel === href?.replace(/\/$/, '');

  if (parsedUrl) {
    let toPath = '';
    let compactLabel = '';

    if (parsedUrl.type === 'post') {
      toPath = buildPostPath(parsedUrl.author, parsedUrl.permlink);
      compactLabel = `@${parsedUrl.author}/${parsedUrl.permlink}`;
    } else if (parsedUrl.type === 'community') {
      toPath = `/c/${parsedUrl.id}`;
      compactLabel = `c/${parsedUrl.id}`;
    } else if (parsedUrl.type === 'tag') {
      toPath = useFeedScopedHashtags
        ? `/feed/${activeFeedType}/tag/${parsedUrl.tag}`
        : `/tag/${parsedUrl.tag}`;
      compactLabel = `#${parsedUrl.tag}`;
    } else if (parsedUrl.type === 'user') {
      toPath = `/user/${parsedUrl.username}`;
      compactLabel = `@${parsedUrl.username}`;
    }

    const shouldCompact = compactLabel && isAutoLinked;

    return (
      <Link {...props} to={toPath}>
        {shouldCompact ? compactLabel : children}
      </Link>
    );
  }

  return (
    <a {...props} href={href} target="_blank" rel="noopener noreferrer">{children}</a>
  );
};

// Module-level cache — persists across re-renders and re-mounts so the same
// post is never fetched more than once per page load.
const embeddedPostCache = new Map();

/**
 * Component to display embedded Hive posts
 */
const EmbeddedPost = React.memo(({ author, permlink, reblogData, fallback }) => {
  const cacheKey = `${author}/${permlink}`;
  const [post, setPost] = useState(() => embeddedPostCache.get(cacheKey) || null);
  const [loading, setLoading] = useState(() => !embeddedPostCache.has(cacheKey));
  const [error, setError] = useState(false);

  const fetchPost = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await postsApi.getPost(author, permlink, false);
      embeddedPostCache.set(cacheKey, data.post);
      setPost(data.post);
    } catch (err) {
      console.error('Error fetching embedded post:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [author, permlink, cacheKey]);

  useEffect(() => {
    if (embeddedPostCache.has(cacheKey)) return; // already cached, skip fetch
    fetchPost();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [author, permlink, cacheKey]);

  if (loading) {
    return (
      <div className="embedded-post loading">
        <div className="embedded-post-loading">Loading post...</div>
      </div>
    );
  }

  if (error || !post) {
    if (fallback) return fallback;
    return (
      <div className="embedded-post error">
        <div className="embedded-post-header">
          <Link to={buildPostPath(author, permlink)}>
            @{author}/{permlink}
          </Link>
        </div>
        <div className="embedded-post-error">Failed to load post</div>
        <button className="embedded-post-reload-btn" onClick={fetchPost} style={{marginTop:8}}>Reload</button>
      </div>
    );
  }

  // Determine linked content type
  const isComment = post.depth > 0 || post.parent_author;
  const isArticle = !isComment;
  
  const images = extractImagesFromPost(post);
  const previewLength = isComment ? 250 : 500;
  const sanitizedBody = stripConfiguredSubAttributionBlocks(post.body || '');
  const bodyPreview = sanitizedBody.length > previewLength ? sanitizedBody.substring(0, previewLength) + '...' : sanitizedBody;
  const targetHref = buildContentPath(post.author, post.permlink, post);

  return (
    <div className="embedded-post">
      <div className="reblog-label">{reblogData ? 'reblogged' : (isComment ? 'linked comment' : 'linked article')}</div>
      <div className="reblog-content">
        <div className="reblog-original-header">
          <Link to={`/user/${post.author}`} className="original-author">
            @{post.author}
          </Link>
        </div>
        <div className="reblog-actions">
          <a
            href={targetHref}
            className="view-reblog-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            @{post.author}/{post.permlink}
          </a>
        </div>
        <div className="reblog-markdown-content">
          <MarkdownRenderer content={bodyPreview} images={images} />
        </div>
        <div className="reblog-actions">
          <Link
            to={targetHref}
            className="view-reblog-link"
            onClick={() => {
              sessionStorage.setItem('scrollPosition', window.scrollY.toString());
              sessionStorage.setItem('preserveScroll', 'true');
            }}
          >
            {isArticle ? 'View full article →' : 'View full comment →'}
          </Link>
        </div>
      </div>
    </div>
  );
});

/**
 * Renders markdown and HTML content safely with proper formatting
 * Supports GitHub Flavored Markdown (tables, strikethrough, etc.)
 * Sanitizes HTML to prevent XSS attacks
 * Can display images using ImageCarousel component
 * Detects and embeds Hive post links
 */
const MarkdownRenderer = ({
  content,
  isHtml = false,
  className = '',
  images = null,
  reblogData = null,
  forceInlineImages = false,
  disableCarousel = false,
  suppressInlineImages = false,
  enableContiguousImageGrid = false
}) => {
  const location = useLocation();
  const { imageDisplayMode = 'carousel' } = useSettings();
  const safeContent = typeof content === 'string' ? content : '';
  const hasRenderableContent = safeContent.trim().length > 0;

  const matchedFeed = location?.pathname?.match(/^\/feed\/([^/]+)/);
  const activeFeedType = matchedFeed && matchedFeed[1] ? matchedFeed[1].toLowerCase() : 'flex';
  const useFeedScopedHashtags = location?.pathname?.startsWith('/feed/')
    || /\/flexdetail(?:\/|$)/.test(location?.pathname || '');
  const userWantsCarousel = imageDisplayMode === 'carousel' && !disableCarousel;

  // Leave <center> tags as-is — they are in the rehype-sanitize allowed list and
  // the CSS rules for `.markdown-renderer center` handle centering including images
  // and carousel placeholders. Leaving them native also means truncated posts
  // (collapsed cards cut off before </center>) work correctly out of the box.
  let processedContent = stripConfiguredSubAttributionBlocks(safeContent);
  processedContent = normalizeMarkdownTableSpacing(processedContent);

  // Normalize older markdown video links into the local video shortcode path.
  processedContent = processedContent.replace(
    /\[Video:\s*([^\]]+)\]\((https?:\/\/[^\s)]+)\)/gi,
    (_m, title, url) => `[video url="${String(url).trim()}" title="${String(title).trim()}"]`
  );

  // Convert standalone plain-text video URL lines to the same embed path used by [video].
  processedContent = processedContent.replace(/^\s*(https?:\/\/[^\s<]+)\s*$/gim, (match, rawUrl) => {
    const url = String(rawUrl || '').trim();
    if (!url || !getVideoEmbedUrl(url)) return match;
    return `<shortappvideo url="${escapeHtml(url)}" title="" align="left"></shortappvideo>`;
  });

  // Convert standalone plain-text image URL lines to <img> so they render consistently
  // in all contexts (feed and detail), even when markdown auto-linking is inconsistent.
  processedContent = processedContent.replace(/^\s*(https?:\/\/[^\s<]+)\s*$/gim, (match, rawUrl) => {
    const url = String(rawUrl || '').trim();
    if (!url || (!isImageUrlByExtension(url) && !isImageUrl(url))) return match;
    return `<img src="${escapeHtml(url)}" alt="" />`;
  });

  // Normalize markdown input from blockchain into shortcode equivalents first,
  // then reuse the shortcode rendering path below.
  processedContent = processedContent.replace(/^######\s+(.+)$/gm, '[h6]$1[/h6]');
  processedContent = processedContent.replace(/^#####\s+(.+)$/gm, '[h5]$1[/h5]');
  processedContent = processedContent.replace(/^####\s+(.+)$/gm, '[h4]$1[/h4]');
  processedContent = processedContent.replace(/^###\s+(.+)$/gm, '[h3]$1[/h3]');
  processedContent = processedContent.replace(/^##\s+(.+)$/gm, '[h2]$1[/h2]');
  processedContent = processedContent.replace(/^#\s+(.+)$/gm, '[h1]$1[/h1]');
  // When [img-url](img-url) has the same image URL in both label and destination,
  // convert to <img> directly so only one render path fires. Without this, the
  // link→<a> step below would produce <a href="url">url</a> where the URL
  // appears twice (in href and as text), causing two images to render.
  processedContent = processedContent.replace(
    /(?<!!)\[(https?:\/\/[^\]\s]+)\]\(\1\)/gi,
    (_m, url) => {
      if (!isImageUrlByExtension(url) && !isImageUrl(url)) return _m;
      return `<img src="${escapeHtml(url)}" alt="" />`;
    }
  );

  // Convert markdown image syntax ![alt](url) to <img> tags so they render
  // inside HTML block elements (<center>, <div>, etc.) where the markdown
  // parser does not process inline markdown. Only convert when the URL
  // resolves to an image (by extension or known Hive CDN hostname).
  processedContent = processedContent.replace(/!\[([^\]]*)\]\(([^)\s]+)\)/g, (_m, alt, src) => {
    if (!isImageUrl(src) && !isImageUrlByExtension(src)) return _m;
    return `<img src="${escapeHtml(src)}" alt="${escapeHtml(alt)}" />`;
  });

  // Convert markdown link syntax [text](url) to <a> tags so they render inside
  // HTML block elements (<center>, <div>, etc.) where the markdown parser does
  // not process inline markdown. The negative lookbehind excludes ![...](...)
  // image syntax already handled above.
  processedContent = processedContent.replace(/(?<!!)(\[([^\]]+)\])\(([^)\s]+)\)/g, (_m, _bracketPart, text, href) => {
    return `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${escapeHtml(text)}</a>`;
  });

  // Standalone markdown thematic breaks (---, ***, ___) → hr. Handled here via
  // regex so they work inside HTML block elements like <center> where the
  // markdown parser would otherwise ignore them.
  processedContent = processedContent.replace(/^[ \t]*(-{3,}|\*{3,}|_{3,})[ \t]*$/gm, '<hr>');
  // Insert <br> at bare-newline line-endings within HTML blocks so text on
  // separate lines inside <center>, <div>, <sub>, etc. renders with visible breaks.
  processedContent = insertHtmlBlockLineBreaks(processedContent);
  processedContent = processedContent.replace(/\*\*([^*\n][\s\S]*?)\*\*/g, '[b]$1[/b]');
  processedContent = processedContent.replace(/\*([^*\n][\s\S]*?)\*/g, '[i]$1[/i]');

  // Convert align shortcodes into simple HTML blocks for predictable rendering.
  processedContent = processedContent.replace(
    /\[align=(left|center|right)\]([\s\S]*?)\[\/align\]/gi,
    (_m, align, inner) => {
      const resolved = String(align).toLowerCase();
      return `<div class="shortapp-align text-${resolved}" align="${resolved}" style="text-align: ${resolved};">${inner}</div>`;
    }
  );
  processedContent = processedContent.replace(/\[hl(\s+[^\]]*)?\]([\s\S]*?)\[\/hl\]/gi, '[highlight$1]$2[/highlight]');
  processedContent = processedContent.replace(/\[video\s+([^\]]+)\]/gi, (_m, attributeText) => {
    const attrs = parseShortcodeAttributes(attributeText);
    const url = attrs.url || attrs.src || '';
    if (!url) return '';
    const title = attrs.title || '';
    const align = normalizeShortcodeAlignment(attrs.align || 'left');
    return `<shortappvideo url="${escapeHtml(url)}" title="${escapeHtml(title)}" align="${align}"></shortappvideo>`;
  });
  processedContent = processedContent.replace(/\[img\s+([^\]]+)\]/gi, (_m, attributeText) => {
    const attrs = parseShortcodeAttributes(attributeText);
    const url = attrs.url || attrs.src || '';
    if (!url) return '';
    const align = normalizeShortcodeAlignment(attrs.align || 'left');
    if (align === 'left') {
      return `<img src="${escapeHtml(url)}" alt="" />`;
    }
    return `<img src="${escapeHtml(url)}" alt="" class="text-${align}" align="${align}" data-align="${align}" style="display: block; margin: ${align === 'center' ? '8px auto' : '8px 0 8px auto'};" />`;
  });
  processedContent = processedContent.replace(/\[link\s+([^\]]+)\]([\s\S]*?)\[\/link\]/gi, (_m, attributeText = '', body = '') => {
    const attrs = parseShortcodeAttributes(attributeText);
    const href = attrs.url || attrs.href || '';
    if (!href) return String(body || '').trim();
    const label = String(body || '').trim() || href;
    // If the body is the same image URL as the href, emit a single <img> so the
    // URL only appears once and doesn't render as both an image and an <a> link.
    if (label === href && (isImageUrlByExtension(href) || isImageUrl(href))) {
      return `<img src="${escapeHtml(href)}" alt="" />`;
    }
    return `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${escapeHtml(label)}</a>`;
  });
  processedContent = processedContent.replace(/\[spoiler(?:\s+([^\]]+))?\]([\s\S]*?)\[\/spoiler\]/gi, (_m, attributeText = '', body) => {
    const attrs = parseShortcodeAttributes(attributeText);
    const title = escapeHtml(attrs.title || 'Spoiler');
    const align = normalizeShortcodeAlignment(attrs.align || 'left');
    const contentHtml = escapeHtml(String(body || '').trim() || 'Hidden content').replace(/\n/g, '<br />');
    return `<div class="shortapp-align text-${align}" align="${align}" style="text-align: ${align};"><details class="markdown-spoiler"><summary>${title}</summary><div>${contentHtml}</div></details></div>`;
  });
  processedContent = processedContent.replace(/\[highlight(?:\s+([^\]]+))?\]([\s\S]*?)\[\/highlight\]/gi, (_m, attributeText = '', body = '') => {
    const attrs = parseShortcodeAttributes(attributeText);
    const textColor = escapeHtml(attrs.color || 'black');
    const bgColor = escapeHtml(attrs.bg || 'yellow');
    const contentHtml = escapeHtml(String(body || '').trim()).replace(/\n/g, '<br />');
    if (!contentHtml) return '';
    return `<span style="background-color: ${bgColor}; color: ${textColor}; padding-left: 2px; padding-right: 2px;">${contentHtml}</span>`;
  });
  processedContent = processedContent.replace(/\[blockquote(?:\s+([^\]]+))?\]([\s\S]*?)\[\/blockquote\]/gi, (_m, attributeText = '', body = '') => {
    const attrs = parseShortcodeAttributes(attributeText);
    const align = normalizeShortcodeAlignment(attrs.align || 'left');
    const contentHtml = escapeHtml(String(body || '').trim())
      .replace(/&lt;br\s*\/?&gt;/gi, '<br />')
      .replace(/\r?\n/g, '<br />');
    return `<blockquote class="text-${align}" align="${align}" data-align="${align}" style="text-align: ${align};">${contentHtml}</blockquote>`;
  });
  processedContent = processedContent.replace(/\[quote(?:\s+([^\]]+))?\]([\s\S]*?)\[\/quote\]/gi, (_m, attributeText = '', body = '') => {
    const attrs = parseShortcodeAttributes(attributeText);
    const align = normalizeShortcodeAlignment(attrs.align || 'left');
    const contentHtml = escapeHtml(String(body || '').trim())
      .replace(/&lt;br\s*\/?&gt;/gi, '<br />')
      .replace(/\r?\n/g, '<br />');
    return `<blockquote class="text-${align}" align="${align}" data-align="${align}" style="text-align: ${align};">${contentHtml}</blockquote>`;
  });
  processedContent = processedContent.replace(/\[b\]([\s\S]*?)\[\/b\]/gi, '<strong>$1</strong>');
  processedContent = processedContent.replace(/\[i\]([\s\S]*?)\[\/i\]/gi, '<em>$1</em>');
  processedContent = processedContent.replace(/\[u\]([\s\S]*?)\[\/u\]/gi, '<u>$1</u>');
  processedContent = processedContent.replace(/\[s\]([\s\S]*?)\[\/s\]/gi, '<s>$1</s>');
  processedContent = processedContent.replace(/\[h1\]([\s\S]*?)\[\/h1\]/gi, '<h1>$1</h1>');
  processedContent = processedContent.replace(/\[h2\]([\s\S]*?)\[\/h2\]/gi, '<h2>$1</h2>');
  processedContent = processedContent.replace(/\[h3\]([\s\S]*?)\[\/h3\]/gi, '<h3>$1</h3>');
  processedContent = processedContent.replace(/\[h4\]([\s\S]*?)\[\/h4\]/gi, '<h4>$1</h4>');
  processedContent = processedContent.replace(/\[h5\]([\s\S]*?)\[\/h5\]/gi, '<h5>$1</h5>');
  processedContent = processedContent.replace(/\[h6\]([\s\S]*?)\[\/h6\]/gi, '<h6>$1</h6>');
  processedContent = collapseContiguousImagesToGrid(processedContent, {
    enabled: enableContiguousImageGrid,
    min: 2,
    max: 6,
  });

  const metadataImageEntries = useMemo(() => normalizeImageEntries(images), [images]);
  const inlineImageEntries = useMemo(() => extractInlineImageSources(processedContent), [processedContent]);
  const extensionLinkImageEntries = useMemo(() => (
    extractLinkUrlsFromContent(processedContent)
      .filter((url) => isImageUrlByExtension(url))
      .map((url) => ({ url, source: 'link-extension' }))
  ), [processedContent]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxImages, setLightboxImages] = useState([]);

  const carouselImages = useMemo(() => {
    const combined = [
      ...metadataImageEntries,
      ...inlineImageEntries,
      ...extensionLinkImageEntries
    ];
    const seen = new Set();
    const deduped = [];
    combined.forEach((entry) => {
      const url = String(entry?.url || '').trim();
      if (!url || seen.has(url)) return;
      seen.add(url);
      deduped.push({ url });
    });
    return deduped.map((entry, index) => ({ ...entry, label: `[Image ${index + 1}]` }));
  }, [metadataImageEntries, inlineImageEntries, extensionLinkImageEntries]);

  const shouldShowCarousel = userWantsCarousel && carouselImages.length > 0 && !forceInlineImages;
  const shouldReplaceInlineImagesWithPlaceholder = shouldShowCarousel && !forceInlineImages;

  const getImageLabelByUrl = useCallback((url) => {
    const normalized = String(url || '').trim();
    if (!normalized) return '[Image]';
    const index = carouselImages.findIndex((item) => item.url === normalized);
    return index >= 0 ? `[Image ${index + 1}]` : '[Image]';
  }, [carouselImages]);

  const openLightboxForUrl = useCallback((url) => {
    const normalized = String(url || '').trim();
    if (!normalized) return;
    const baseImages = carouselImages.map((item) => item.url).filter(Boolean);
    const list = baseImages.includes(normalized) ? baseImages : [normalized, ...baseImages];
    const index = Math.max(0, list.findIndex((item) => item === normalized));
    setLightboxImages(list);
    setLightboxIndex(index);
    setLightboxOpen(true);
  }, [carouselImages]);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  const previousLightboxImage = useCallback(() => {
    setLightboxIndex((prev) => (
      prev === 0 ? Math.max(0, lightboxImages.length - 1) : prev - 1
    ));
  }, [lightboxImages.length]);

  const nextLightboxImage = useCallback(() => {
    setLightboxIndex((prev) => (
      prev === Math.max(0, lightboxImages.length - 1) ? 0 : prev + 1
    ));
  }, [lightboxImages.length]);

  if (!hasRenderableContent) return null;


  const sanitizeSchema = {
    ...defaultSchema,
    tagNames: Array.from(new Set([
      ...(defaultSchema.tagNames || []),
      'center', 'sub', 'sup', 'iframe', 'video', 'source', 'details', 'summary', 'shortappvideo',
      'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'shortappimagegrid', 'shortappgridimg'
    ])),
    attributes: {
      ...(defaultSchema.attributes || {}),
      a: ['href', 'title', 'target', 'rel'],
      img: ['src', 'alt', 'title', 'align', 'class', 'style', 'data-align'],
      shortappimagegrid: ['count', 'class'],
      shortappgridimg: ['src', 'alt', 'title'],
      table: ['class', 'style'],
      thead: ['class', 'style'],
      tbody: ['class', 'style'],
      tfoot: ['class', 'style'],
      tr: ['class', 'style'],
      th: ['colspan', 'rowspan', 'align', 'class', 'style'],
      td: ['colspan', 'rowspan', 'align', 'class', 'style'],
      div: ['align', 'class', 'style'],
      p: ['align'],
      span: ['style', 'class', 'align'],
      h1: ['align'],
      h2: ['align'],
      h3: ['align'],
      h4: ['align'],
      h5: ['align'],
      blockquote: ['align', 'class', 'style', 'data-align'],
      iframe: ['src', 'frameborder', 'allow', 'allowfullscreen', 'title'],
      video: ['src', 'controls', 'loop', 'muted', 'playsinline'],
      source: ['src', 'type'],
      details: ['className', 'class', 'align', 'style', 'data-align'],
      shortappvideo: ['url', 'title', 'align']
    }
  };

  // For HTML content, sanitize and render
  if (isHtml) {
    const cleanHtml = DOMPurify.sanitize(processedContent, {
      ALLOWED_TAGS: [
        'p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'ul', 'ol', 'li', 'a', 'img', 'blockquote', 'code', 'pre',
        'table', 'thead', 'tbody', 'tr', 'th', 'td', 'div', 'span',
        'center', 'sub', 'sup'
      ],
      ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'target', 'rel', 'colspan', 'rowspan', 'align', 'class', 'style', 'data-align']
    });

    return (
      <div 
        className={`markdown-renderer html-content ${className}`}
        dangerouslySetInnerHTML={{ __html: cleanHtml }}
      />
    );
  }

  // Helper to render text with inline images, @mentions, and #hashtags.
  // Bare image URLs (https://...gif etc.) are rendered as <ResilientImage>, not as links.
  const renderTextWithMentionsAndTags = (text, context = 'inline') => {
    if (!text || typeof text !== 'string') return text;

    // Pattern priority:
    // 1. URL: Matches http/https (Trailing punctuation is safely trimmed in the loop)
    // 2. @user/permlink: permlink naturally excludes dots and commas
    // 3. @mention: Allows internal dots, but GUARANTEES it does not end with a dot
    // 4. #hashtag: naturally excludes dots and commas
    const pattern = /(https?:\/\/[^\s<>")\]]+)|(@[a-zA-Z0-9._-]+\/[a-zA-Z0-9-]+)|(@[a-zA-Z0-9_-]+(?:\.[a-zA-Z0-9_-]+)*)|#([a-zA-Z0-9-]+)/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = pattern.exec(text)) !== null) {
      // FIX FOR URLs: If a URL greedily consumed sentence punctuation, give it back!
      if (match[1] && /[.,!?:;]$/.test(match[1])) {
        match[1] = match[1].slice(0, -1); // Remove punctuation from the URL match
        pattern.lastIndex--; // Step regex back by 1 so the punctuation renders as normal text
      }

      // Add normal text before the match
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }

      if (match[1]) {
        // Group 1: URL
        const url = match[1];
        if (typeof suppressInlineImages !== 'undefined' && !suppressInlineImages && isImageUrlByExtension(url)) {
          parts.push(
            <ResilientImage
              key={`img-${match.index}`}
              src={url}
              alt=""
              style={{ maxWidth: '100%', height: 'auto', display: 'block', margin: '8px 0', cursor: 'zoom-in' }}
              onClick={() => openLightboxForUrl(url)}
            />
          );
        } else {
          parts.push(
            <a key={`url-${match.index}`} href={url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-color)' }}>
              {url}
            </a>
          );
        }
      } else if (match[2]) {
        // Group 2: @user/permlink
        const withoutAt = match[2].substring(1);
        const [user, permlink] = withoutAt.split('/');
        parts.push(
          <Link key={`postlink-${match.index}`} to={buildPostPath(user, permlink)} style={{ color: 'var(--primary-color)', textDecoration: 'none' }}>
            {match[2]}
          </Link>
        );
      } else if (match[3]) {
        // Group 3: @mention
        const username = match[3].substring(1); // Safely remove @
        parts.push(
          <Link key={`mention-${match.index}`} to={`/user/${username}`} style={{ color: 'var(--primary-color)', textDecoration: 'none' }}>
            {match[3]}
          </Link>
        );
      } else if (match[4]) {
        // Group 4: #hashtag
        const hashtag = match[4];
        const hashtagHref = useFeedScopedHashtags
          ? `/feed/${activeFeedType}/tag/${encodeURIComponent(hashtag)}`
          : `/tag/${encodeURIComponent(hashtag)}`;
        parts.push(
          <Link key={`tag-${match.index}`} to={hashtagHref} style={{ color: 'var(--primary-color)', textDecoration: 'none' }}>
            #{hashtag}
          </Link>
        );
      }

      // Update lastIndex to the end of the current match (respecting our manual step-back for URLs)
      lastIndex = pattern.lastIndex;
    }

    // Add any remaining trailing text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts.length > 0 ? parts : text;
  };

  // Helper: process direct string children for inline images, @mentions and #hashtags.
  // Each element handler calls this so nested inline elements (strong, em, etc.)
  // each process their own string children - covering all nesting depths.
  const processMentions = (children) =>
    React.Children.map(children, (child) =>
      typeof child === 'string' ? renderTextWithMentionsAndTags(child) : child
    )?.flat();

  // For markdown content, use react-markdown with GFM
  return (
    <div className={`markdown-renderer markdown-content ${className}`}>
      <ReactMarkdown 
        remarkPlugins={[remarkGfm, remarkBreaks]}
        rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
        components={{
          // Custom paragraph handler - render @mentions and #hashtags
          p: ({ node, children, ...props }) => {
            const childrenArray = React.Children.toArray(children);
            const paragraphText = childrenArray.length === 1
              ? flattenNodeText(childrenArray[0]).trim()
              : null;
            const parsedStandalonePostRef = extractStandalonePostRefFromParagraphNode(node);

            const paragraphIsHttpUrl = Boolean(paragraphText && /^https?:\/\//i.test(paragraphText));
            const paragraphIsVideoUrl = paragraphIsHttpUrl && isVideoUrlByExtension(paragraphText);
            const paragraphIsImageUrl = paragraphIsHttpUrl && isImageUrlByExtension(paragraphText);

            // Audio 3Speak embed
            const paragraphAudioEmbed = paragraphIsHttpUrl ? renderAudio3SpeakEmbed(paragraphText, '') : null;
            if (paragraphAudioEmbed) {
              return paragraphAudioEmbed;
            }

            const paragraphVideoEmbed = paragraphIsHttpUrl ? renderVideoEmbed(paragraphText, '') : null;
            if (paragraphVideoEmbed) {
              return paragraphVideoEmbed;
            }

            if (paragraphIsVideoUrl) {
              return (
                <video
                  className="markdown-direct-video"
                  src={paragraphText}
                  controls
                  playsInline
                  preload="metadata"
                />
              );
            }

            if (paragraphIsImageUrl) {
              if (suppressInlineImages) {
                return null;
              }
              if (shouldReplaceInlineImagesWithPlaceholder) {
                return <span className="inline-image-placeholder">{getImageLabelByUrl(paragraphText)}</span>;
              }
              return (
                <ResilientImage
                  src={paragraphText}
                  alt={getImageLabelByUrl(paragraphText)}
                  title={getImageLabelByUrl(paragraphText)}
                  style={{ maxWidth: '100%', height: 'auto', display: 'block', margin: '8px 0', cursor: 'zoom-in' }}
                  onClick={() => openLightboxForUrl(paragraphText)}
                />
              );
            }

            // Image checks must run first so post URL parsing does not override image handling.
            const parsedRefFromText = paragraphText && !paragraphIsImageUrl ? parseHiveUrl(paragraphText) : null;
            const parsedRef = parsedStandalonePostRef || parsedRefFromText;
            if (parsedRef && parsedRef.type === 'post') {
              return (
                <EmbeddedPost 
                  author={parsedRef.author} 
                  permlink={parsedRef.permlink} 
                  reblogData={reblogData} 
                  fallback={<p {...props}>{processMentions(children)}</p>}
                />
              );
            }

            return <p {...props}>{processMentions(children)}</p>;
          },
          li: ({ node, children, ...props }) => <li {...props}>{processMentions(children)}</li>,
          strong: ({ node, children, ...props }) => <strong {...props}>{processMentions(children)}</strong>,
          em: ({ node, children, ...props }) => <em {...props}>{processMentions(children)}</em>,
          h1: ({ node, children, ...props }) => <h1 {...props}>{processMentions(children)}</h1>,
          h2: ({ node, children, ...props }) => <h2 {...props}>{processMentions(children)}</h2>,
          h3: ({ node, children, ...props }) => <h3 {...props}>{processMentions(children)}</h3>,
          h4: ({ node, children, ...props }) => <h4 {...props}>{processMentions(children)}</h4>,
          h5: ({ node, children, ...props }) => <h5 {...props}>{processMentions(children)}</h5>,
          h6: ({ node, children, ...props }) => <h6 {...props}>{processMentions(children)}</h6>,
          div: ({ node, children, ...props }) => {
            // Detect image-only divs (e.g. actifit app-store badge rows):
            // if every meaningful AST child is an <img> or an <a> whose sole
            // child is an <img>, automatically add "text-center" so the images
            // centre and the ::after arrow aligns to the image midpoint.
            const isImageOnlyNode = (() => {
              const astChildren = Array.isArray(node?.children) ? node.children : [];
              // Filter out pure whitespace text nodes
              const meaningful = astChildren.filter((c) => {
                if (c.type === 'text') return String(c.value || '').trim().length > 0;
                return c.type === 'element';
              });
              if (meaningful.length === 0) return false;
              return meaningful.every((c) => {
                if (c.type !== 'element') return false;
                const tag = String(c.tagName || '').toLowerCase();
                if (tag === 'img') return true;
                if (tag === 'a') {
                  // anchor wrapping a single img
                  const aKids = (Array.isArray(c.children) ? c.children : []).filter(
                    (k) => !(k.type === 'text' && String(k.value || '').trim().length === 0)
                  );
                  return aKids.length === 1 && String(aKids[0]?.tagName || '').toLowerCase() === 'img';
                }
                return false;
              });
            })();

            const existingClass = String(props.className || '');
            const alreadyCentered = /\btext-center\b/.test(existingClass);
            const className = isImageOnlyNode && !alreadyCentered
              ? `${existingClass} text-center`.trim()
              : existingClass || undefined;

            return <div {...props} className={className}>{processMentions(children)}</div>;
          },
          span: ({ node, children, ...props }) => <span {...props}>{processMentions(children)}</span>,
          center: ({ node, children, ...props }) => <center {...props}>{processMentions(children)}</center>,
          iframe: ({ node, allow, src, title, width, height, ...props }) => {
            const normalizedSrc = stripAutoplayFromUrl(src);
            const normalizedAllow = stripAutoplayFromAllow(allow);
            // Wrap in responsive container so the embed can't overflow the mobile viewport
            // and subsequent text (hashtags, mentions) starts on a new line.
            return (
              <div className="video-embed-wrapper">
                <div className="video-embed-container">
                  <iframe
                    {...props}
                    src={normalizedSrc}
                    allow={normalizedAllow || undefined}
                    title={title || 'embedded content'}
                    frameBorder="0"
                    allowFullScreen
                    loading="lazy"
                  />
                </div>
              </div>
            );
          },
          video: ({ node, autoPlay, autoplay, ...props }) => (
            <video {...props} autoPlay={false} />
          ),
          td: ({ node, children, ...props }) => {
            const cellText = flattenNodeText(children).trim();
            if (cellText && /^https?:\/\//i.test(cellText) && isImageUrlByExtension(cellText)) {
              return (
                <td {...props}>
                  <ResilientImage
                    src={cellText}
                    alt=""
                    style={{ maxWidth: '100%', height: 'auto', display: 'block', cursor: 'zoom-in' }}
                    onClick={() => openLightboxForUrl(cellText)}
                  />
                </td>
              );
            }
            return <td {...props}>{processMentions(children)}</td>;
          },
          th: ({ node, children, ...props }) => {
            const cellText = flattenNodeText(children).trim();
            if (cellText && /^https?:\/\//i.test(cellText) && isImageUrlByExtension(cellText)) {
              return (
                <th {...props}>
                  <ResilientImage
                    src={cellText}
                    alt=""
                    style={{ maxWidth: '100%', height: 'auto', display: 'block', cursor: 'zoom-in' }}
                    onClick={() => openLightboxForUrl(cellText)}
                  />
                </th>
              );
            }
            return <th {...props}>{processMentions(children)}</th>;
          },
          // Raw HTML inline elements that can contain mention/hashtag text
          sup: ({ node, children, ...props }) => <sup {...props}>{processMentions(children)}</sup>,
          sub: ({ node, children, ...props }) => <sub {...props}>{processMentions(children)}</sub>,
          b: ({ node, children, ...props }) => <b {...props}>{processMentions(children)}</b>,
          i: ({ node, children, ...props }) => <i {...props}>{processMentions(children)}</i>,
          u: ({ node, children, ...props }) => <u {...props}>{processMentions(children)}</u>,
          s: ({ node, children, ...props }) => <s {...props}>{processMentions(children)}</s>,
          del: ({ node, children, ...props }) => <del {...props}>{processMentions(children)}</del>,
          mark: ({ node, children, ...props }) => <mark {...props}>{processMentions(children)}</mark>,
          // Links from shortcode/markdown should always render as external links.
          // Exception: if the link was auto-generated from a bare image URL (link text === href),
          // render it as an inline image (or placeholder + carousel entry in carousel mode).
          // Wrap in InsideLinkContext so nested <img> handlers know not to open the lightbox.
          a: ({ node, children, href, ...props }) => (
            <InsideLinkContext.Provider value={true}>
              <SmartExternalLink
                href={href}
                children={children}
                props={props}
                suppressInlineImages={suppressInlineImages}
                carouselMode={shouldReplaceInlineImagesWithPlaceholder}
                imageLabel={getImageLabelByUrl(href)}
                onOpenImage={openLightboxForUrl}
              />
            </InsideLinkContext.Provider>
          ),
          shortappvideo: ({ node, url, title, align }) => {
            const href = url || node?.properties?.url || '';
            const videoTitle = title || node?.properties?.title || '';
            const videoAlign = normalizeShortcodeAlignment(align || node?.properties?.align || 'left');
            if (!href) return null;
            return (
              <div className={`shortapp-align text-${videoAlign}`}>
                <VideoEmbed href={String(href)} title={String(videoTitle || '')} />
              </div>
            );
          },
          shortappimagegrid: ({ node }) => {
            const childNodes = Array.isArray(node?.children) ? node.children : [];
            const gridImages = childNodes
              .map((child) => String(child?.properties?.src || '').trim())
              .filter(Boolean)
              .slice(0, 6);
            if (gridImages.length < 2) return null;

            const gridClassName = `snap-image-grid snap-image-grid-${Math.min(gridImages.length, 6)}`;

            return (
              <div className={gridClassName}>
                {gridImages.map((src, idx) => (
                  <a
                    key={`${src}-${idx}`}
                    href={src}
                    className="snap-grid-item"
                    onClick={(event) => {
                      event.preventDefault();
                      openLightboxForUrl(src);
                    }}
                  >
                    <ResilientImage
                      src={src}
                      alt={`Image ${idx + 1}`}
                      className="snap-image"
                    />
                  </a>
                ))}
              </div>
            );
          },
          // Custom image handler - hide markdown images if we're using ImageCarousel.
          // When nested inside an <a> element (InsideLinkContext), skip the lightbox
          // so the parent anchor's navigation takes effect uninterrupted.
          img: ({ node, src, alt, ...props }) => {
            // eslint-disable-next-line react-hooks/rules-of-hooks
            const insideLink = useContext(InsideLinkContext);
            if (suppressInlineImages) return null;
            if (shouldReplaceInlineImagesWithPlaceholder) {
              return <span className="inline-image-placeholder">{getImageLabelByUrl(src)}</span>;
            }
            return (
              <ResilientImage
                src={src}
                alt={alt}
                title={alt || undefined}
                style={{ cursor: insideLink ? 'pointer' : 'zoom-in' }}
                onClick={insideLink ? undefined : () => openLightboxForUrl(src)}
                {...props}
              />
            );
          },
          // Code block styling
          code: ({ node, inline, ...props }) => (
            inline 
              ? <code className="inline-code" {...props} />
              : <code className="code-block" {...props} />
          ),
          // Blockquote styling
          blockquote: ({ node, children, ...props }) => {
            const blockText = flattenNodeText(children).trim();
            if (blockText.startsWith('!')) {
              const spoilerText = blockText.replace(/^!\s*/, '').trim();
              return (
                <details className="markdown-spoiler" {...props}>
                  <summary>Spoiler</summary>
                  <div>{spoilerText}</div>
                </details>
              );
            }
            return <blockquote className="markdown-blockquote" {...props}>{children}</blockquote>;
          },
          // Table styling
          table: ({ node, ...props }) => (
            <div className="table-wrapper">
              <table className="markdown-table" {...props} />
            </div>
          ),
        }}
      >
        {processedContent}
      </ReactMarkdown>
      {shouldShowCarousel && (
        <>
          <div className="content-media-separator" role="note" aria-label="Images in Carousel below post content">
            <span>Images in Carousel</span>
          </div>
          <ImageCarousel images={carouselImages} />
        </>
      )}
      {lightboxOpen && lightboxImages.length > 0 && (
        <Lightbox
          images={lightboxImages}
          currentIndex={lightboxIndex}
          onClose={closeLightbox}
          onPrevious={previousLightboxImage}
          onNext={nextLightboxImage}
        />
      )}
    </div>
  );
};

const areImagesEqual = (a, b) => {
  if (a === b) return true;
  if (!a && !b) return true;
  if (!a || !b) return false;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

const MemoMarkdownRenderer = React.memo(MarkdownRenderer, (prev, next) => {
  return (
    prev.content === next.content &&
    prev.isHtml === next.isHtml &&
    prev.className === next.className &&
    areImagesEqual(prev.images, next.images) &&
    prev.forceInlineImages === next.forceInlineImages &&
    prev.disableCarousel === next.disableCarousel &&
    prev.suppressInlineImages === next.suppressInlineImages &&
    prev.enableContiguousImageGrid === next.enableContiguousImageGrid &&
    JSON.stringify(prev.reblogData || null) === JSON.stringify(next.reblogData || null)
  );
});

export default MemoMarkdownRenderer;
